import { Col, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import { 
  FiCalendar, 
  FiGift, 
  FiAward, 
  FiActivity,
  FiBell,
  FiClock
} from "react-icons/fi";
import {
  fetchTodayLeave,
  fetchUpcomingBirthdays,
  fetchWorkAnniversaries,
  fetchRecentActivities,
  fetchAnnouncements,
  TodayLeaveData,
  BirthdayData,
  AnniversaryData,
  ActivityData,
  AnnouncementData,
} from "../services/DashboardDataService";
// import { getNotificationsAPI } from "../api/NotificationAPI";

const DashboardWidgets = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [todayLeave, setTodayLeave] = useState<TodayLeaveData[]>([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState<BirthdayData[]>([]);
  const [workAnniversaries, setWorkAnniversaries] = useState<AnniversaryData[]>([]);
  const [recentActivities, setRecentActivities] = useState<ActivityData[]>([]);
  const [announcements, setAnnouncements] = useState<AnnouncementData[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [
          leaveData,
          birthdayData,
          anniversaryData,
          activityData,
          announcementData,
        ] = await Promise.all([
          fetchTodayLeave(),
          fetchUpcomingBirthdays(),
          fetchWorkAnniversaries(),
          fetchRecentActivities(),
          fetchAnnouncements(),
        ]);

        setTodayLeave(leaveData);
        setUpcomingBirthdays(birthdayData);
        setWorkAnniversaries(anniversaryData);
        setRecentActivities(activityData);
        setAnnouncements(announcementData);
      } catch (err) {
        console.error('Error loading widget data:', err);
        setError('Failed to load widget data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="widgets-container">
        {[...Array(6)].map((_, index) => (
          <div key={index} className="glass-card widget-card">
            <div className="widget-title">Loading...</div>
            <div className="text-muted small text-center py-3">Loading data...</div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-4 text-center text-danger">
        {error}
      </div>
    );
  }

  return (
    <div className="widgets-container">
      {/* Today's Leave */}
      <div className="glass-card widget-card">
        <div className="widget-title">
          <FiCalendar className="text-primary" />
          Upcoming Leaves (Next 5 Days)
        </div>
        {todayLeave.length === 0 ? (
          <div className="text-muted small text-center py-3">
            No employees on leave today
          </div>
        ) : (
          <div className="widget-scroll-container">
            {todayLeave.map((item, index) => (
              <div key={index} className="widget-item">
                <div className="widget-info">
                  <div className="widget-name">{item.name}</div>
                  <div className="widget-detail">
                    {item.type} • {item.duration}
                  </div>
                </div>
                <span className="badge badge-warning">Away</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming Birthdays */}
      <div className="glass-card widget-card">
        <div className="widget-title">
          <FiGift className="text-pink" />
          Upcoming Birthdays
        </div>
        {upcomingBirthdays.length === 0 ? (
          <div className="text-muted small text-center py-3">
            No upcoming birthdays this month
          </div>
        ) : (
          <div className="widget-scroll-container">
            {upcomingBirthdays.map((item, index) => (
              <div key={index} className="widget-item">
                <div className="widget-info">
                  <div className="widget-name">{item.name}</div>
                  <div className="widget-detail">
                    {item.department} • {item.date}
                  </div>
                </div>
                <span className="badge badge-success">🎂</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Work Anniversaries */}
      <div className="glass-card widget-card">
        <div className="widget-title">
          <FiAward className="text-warning" />
          Work Anniversaries
        </div>
        {workAnniversaries.length === 0 ? (
          <div className="text-muted small text-center py-3">
            No work anniversaries this month
          </div>
        ) : (
          <div className="widget-scroll-container">
            {workAnniversaries.map((item, index) => (
              <div key={index} className="widget-item">
                <div className="widget-info">
                  <div className="widget-name">{item.name}</div>
                  <div className="widget-detail">
                    {item.years} Years • {item.department}
                  </div>
                </div>
                <span className="badge badge-info">🏆</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Activities */}
      <div className="glass-card widget-card">
        <div className="widget-title">
          <FiActivity className="text-info" />
          Recent Activities
        </div>
        {recentActivities.length === 0 ? (
          <div className="text-muted small text-center py-3">
            Coming soon...
          </div>
        ) : (
          <div className="timeline">
            {recentActivities.map((item, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-time">{item.time}</div>
                <div className="timeline-content">{item.action}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Company Announcements */}
      <div className="glass-card widget-card">
        <div className="widget-title">
          <FiBell className="text-danger" />
          Announcements
        </div>
        {announcements.length === 0 ? (
          <div className="text-muted small text-center py-3">
            No announcements
          </div>
        ) : (
          <div className="widget-scroll-container">
            {announcements.map((item, index) => (
              <div key={index} className="widget-item">
                <div className="widget-info">
                  <div className="widget-name">{item.title}</div>
                  <div className="widget-detail">{item.date}</div>
                </div>
                <span className={`badge badge-${item.priority}`}>
                  {item.priority}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {/* <div className="glass-card widget-card">
        <div className="widget-title">
          <FiClock className="text-success" />
          Quick Stats
        </div>
        <Row className="g-2">
          <Col xs={6}>
            <div className="widget-item" style={{ justifyContent: 'center', flexDirection: 'column' }}>
              <div className="kpi-value" style={{ fontSize: '1.5rem' }}>92%</div>
              <div className="widget-detail">Attendance Rate</div>
            </div>
          </Col>
          <Col xs={6}>
            <div className="widget-item" style={{ justifyContent: 'center', flexDirection: 'column' }}>
              <div className="kpi-value" style={{ fontSize: '1.5rem' }}>87%</div>
              <div className="widget-detail">Project Completion</div>
            </div>
          </Col>
          <Col xs={6}>
            <div className="widget-item" style={{ justifyContent: 'center', flexDirection: 'column' }}>
              <div className="kpi-value" style={{ fontSize: '1.5rem' }}>4.2</div>
              <div className="widget-detail">Avg. Rating</div>
            </div>
          </Col>
          <Col xs={6}>
            <div className="widget-item" style={{ justifyContent: 'center', flexDirection: 'column' }}>
              <div className="kpi-value" style={{ fontSize: '1.5rem' }}>15</div>
              <div className="widget-detail">Open Tickets</div>
            </div>
          </Col>
        </Row>
      </div> */}
    </div>
  );
};

export default DashboardWidgets;
